/**
 * ============================================================================
 * COMPOSABLE: Автосохранение
 * ============================================================================
 * Переиспользуемый composable для автосохранения данных
 * ============================================================================
 */

import { ref, onMounted, onUnmounted } from 'vue'
import { useSaveIndicator } from './useSaveIndicator'

export function useAutoSave(saveFunction, options = {}) {
	const {
		interval = 30000, // 30 секунд
		debounce = 2000,  // 2 секунды
		storageKey = null,
		enabled = true,
		onSaveSuccess = null,
		onSaveError = null,
	} = options

	const { setSaving, setSaved, setUnsaved, setError } = useSaveIndicator()
	
	const isSaving = ref(false)
	const lastSaved = ref(null)
	const hasUnsaved = ref(false)
	const saveError = ref(null)
	
	let intervalTimer = null
	let debounceTimer = null

	/**
	 * Выполнить сохранение
	 */
	async function save() {
		if (isSaving.value || !enabled) return

		try {
			isSaving.value = true
			setSaving()
			saveError.value = null

			await saveFunction()

			lastSaved.value = Date.now()
			hasUnsaved.value = false
			setSaved()

			if (onSaveSuccess) {
				onSaveSuccess()
			}

			console.log('✅ [AutoSave] Данные сохранены')
		} catch (error) {
			console.error('❌ [AutoSave] Ошибка сохранения:', error)
			saveError.value = error
			setError()

			if (onSaveError) {
				onSaveError(error)
			}
		} finally {
			isSaving.value = false
		}
	}

	/**
	 * Запланировать сохранение с debounce
	 */
	function scheduleSave() {
		if (!enabled) return

		hasUnsaved.value = true
		setUnsaved()

		if (debounceTimer) {
			clearTimeout(debounceTimer)
		}

		debounceTimer = setTimeout(() => {
			save()
		}, debounce)
	}

	/**
	 * Сохранить в localStorage
	 */
	function saveToLocalStorage(data) {
		if (!storageKey) return

		try {
			const saveData = {
				data,
				timestamp: Date.now(),
			}
			localStorage.setItem(storageKey, JSON.stringify(saveData))
			console.log('💾 [AutoSave] Сохранено в localStorage:', storageKey)
		} catch (error) {
			console.error('❌ [AutoSave] Ошибка сохранения в localStorage:', error)
		}
	}

	/**
	 * Загрузить из localStorage
	 */
	function loadFromLocalStorage() {
		if (!storageKey) return null

		try {
			const saved = localStorage.getItem(storageKey)
			if (!saved) return null

			const { data, timestamp } = JSON.parse(saved)
			console.log('📂 [AutoSave] Загружено из localStorage:', storageKey)
			
			return { data, timestamp }
		} catch (error) {
			console.error('❌ [AutoSave] Ошибка загрузки из localStorage:', error)
			return null
		}
	}

	/**
	 * Очистить localStorage
	 */
	function clearLocalStorage() {
		if (!storageKey) return

		try {
			localStorage.removeItem(storageKey)
			console.log('🗑️ [AutoSave] Очищен localStorage:', storageKey)
		} catch (error) {
			console.error('❌ [AutoSave] Ошибка очистки localStorage:', error)
		}
	}

	/**
	 * Запустить периодическое автосохранение
	 */
	function startAutoSave() {
		if (intervalTimer || !enabled) return

		intervalTimer = setInterval(() => {
			if (hasUnsaved.value) {
				save()
			}
		}, interval)

		console.log('✅ [AutoSave] Автосохранение запущено (интервал:', interval, 'мс)')
	}

	/**
	 * Остановить автосохранение
	 */
	function stopAutoSave() {
		if (intervalTimer) {
			clearInterval(intervalTimer)
			intervalTimer = null
		}

		if (debounceTimer) {
			clearTimeout(debounceTimer)
			debounceTimer = null
		}

		console.log('🛑 [AutoSave] Автосохранение остановлено')
	}

	onMounted(() => {
		if (enabled) {
			startAutoSave()
		}
	})

	onUnmounted(() => {
		stopAutoSave()
	})

	return {
		save,
		scheduleSave,
		isSaving,
		lastSaved,
		hasUnsaved,
		saveError,
		saveToLocalStorage,
		loadFromLocalStorage,
		clearLocalStorage,
		startAutoSave,
		stopAutoSave,
	}
}
